import { Target } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useGoals, useUpsertPersonalGoal, useUpsertTeamGoal, type UpsertGoalPayload } from "@/hooks/useGoals";
import { GoalEditDialog } from "./GoalEditDialog";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";

interface GoalSectionProps {
  /** Page-level view mode: "all" = team scope, "my" = personal scope */
  viewMode: "my" | "all";
}

export function GoalSection({ viewMode }: GoalSectionProps) {
  const currentYear = new Date().getFullYear();
  const { data: goalsData, isLoading, error, refetch } = useGoals(currentYear);

  const hasTeamAccess = goalsData?.showTeamByDefault ?? false;
  const canEditTeamGoals = goalsData?.canEditTeamGoals ?? false;
  const showNetGoal = goalsData?.canEditNetGoal ?? false;

  const upsertPersonal = useUpsertPersonalGoal();
  const upsertTeam = useUpsertTeamGoal();

  if (error) {
    return (
      <SectionLoadError
        sectionName="Goal Tracker"
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading) {
    return (
      <section className="rounded-xl border bg-card p-4 space-y-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-4 w-60" />
      </section>
    );
  }

  const goalScope: "team" | "personal" =
    viewMode === "all" && hasTeamAccess ? "team" : "personal";

  const activeGoal = goalScope === "team" ? goalsData?.team : goalsData?.personal;
  const goalAmount = parseFloat(activeGoal?.commissionGoal ?? "0");
  const salesGoalAmount = parseFloat(activeGoal?.salesGoal ?? "0");
  const netGoalAmount = showNetGoal ? parseFloat(activeGoal?.netCommissionGoal ?? "0") : 0;

  const scopeLabel = goalScope === "team" ? "Team" : "Personal";
  const hasGoals =
    goalAmount > 0 ||
    netGoalAmount > 0 ||
    salesGoalAmount > 0 ||
    (activeGoal?.tripsTraveledGoal ?? 0) > 0;
  const canEditGoals = goalScope === "personal" || (goalScope === "team" && canEditTeamGoals);

  const goalEditDialogProps =
    goalScope === "personal"
      ? {
          title: "Personal Goals",
          description: "Set your personal targets for the year.",
          goal: goalsData?.personal ?? null,
          onSave: async (p: UpsertGoalPayload) => {
            await upsertPersonal.mutateAsync(p);
          },
          isSaving: upsertPersonal.isPending,
          showNetCommissionGoal: showNetGoal,
        }
      : {
          title: "Team Goals",
          description: "Set team-wide targets for the year.",
          goal: goalsData?.team ?? null,
          onSave: async (p: UpsertGoalPayload) => {
            await upsertTeam.mutateAsync(p);
          },
          isSaving: upsertTeam.isPending,
          showNetCommissionGoal: showNetGoal,
        };

  return (
    <section aria-labelledby="goal-section-heading" className="rounded-xl border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-muted-foreground" aria-hidden />
          <h2 id="goal-section-heading" className="text-sm font-semibold">
            {currentYear} {scopeLabel} Goal Tracker
          </h2>
        </div>

        {hasGoals && canEditGoals && (
          <div className="flex items-center gap-1">
            <GoalEditDialog {...goalEditDialogProps} year={currentYear} />
          </div>
        )}
      </div>

      {hasGoals ? (
        <div
          className={`grid grid-cols-1 gap-3 ${
            showNetGoal ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"
          }`}
        >
          <GoalCard label="Sales Goal" value={salesGoalAmount} />
          <GoalCard
            label={showNetGoal ? "Gross Commission Goal" : "Commission Goal"}
            value={goalAmount}
          />
          {showNetGoal && <GoalCard label="Net Commission Goal" value={netGoalAmount} />}
          <GoalCard
            label="Trips Traveled Goal"
            value={activeGoal?.tripsTraveledGoal ?? 0}
            isCurrency={false}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 py-6 text-sm text-muted-foreground">
          <p>No {scopeLabel.toLowerCase()} goals set for {currentYear}.</p>
          {canEditGoals && (
            <GoalEditDialog
              {...goalEditDialogProps}
              year={currentYear}
              triggerLabel="Add Goals"
            />
          )}
        </div>
      )}
    </section>
  );
}

function GoalCard({
  label,
  value,
  isCurrency = true,
}: {
  label: string;
  value: number;
  isCurrency?: boolean;
}) {
  const display = isCurrency
    ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : value.toLocaleString();

  return (
    <div className="rounded-lg border bg-muted/40 px-3 py-2.5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
        {label}
      </p>
      <p className="text-lg font-bold tracking-tight">{display}</p>
    </div>
  );
}
